import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input, Select, MultiSelect } from '..';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiSmartphone,
  FiMapPin,
  FiFileText,
  FiBriefcase,
  FiEdit,
  FiHash,
  FiFlag,
  FiHome,
  FiTruck,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import '../CompanyForm/style.css';

export default function SupplierForm({ companyId, companies = [], initialData, onSuccess, onCancel }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    company_id: '',
    type: 'PJ',
    name: '',
    trade_name: '',
    document: '',
    email: '',
    phone: '',
    mobile: '',
    zip_code: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    notes: '',
    active: true,
    is_internal: false,
  });
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        company_id: initialData.company_id ? String(initialData.company_id) : (initialData.company?.id ? String(initialData.company.id) : (companyId ? String(companyId) : '')),
        type: initialData.type || 'PJ',
        name: initialData.name || '',
        trade_name: initialData.trade_name || '',
        document: initialData.document || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        mobile: initialData.mobile || '',
        zip_code: initialData.zip_code || '',
        street: initialData.street || '',
        number: initialData.number || '',
        complement: initialData.complement || '',
        neighborhood: initialData.neighborhood || '',
        city: initialData.city || '',
        state: initialData.state || '',
        notes: initialData.notes || '',
        active: initialData.active ?? true,
        is_internal: initialData.is_internal ?? false,
      });
      const initialCompanyId = initialData.company_id || initialData.company?.id || companyId;
      setSelectedCompanyIds(initialCompanyId ? [String(initialCompanyId)] : []);
    } else {
      const defaultCompanyId = companyId ? String(companyId) : '';
      setFormData(prev => ({
        ...prev,
        company_id: defaultCompanyId,
      }));
      setSelectedCompanyIds(defaultCompanyId ? [defaultCompanyId] : []);
    }
  }, [initialData, companyId]);

  const companyOptions = useMemo(() => {
    return companies.map(c => ({
      value: String(c.id),
      label: c.trade_name || c.name,
    }));
  }, [companies]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }

  function handleTypeChange(event) {
    setFormData(prev => ({
      ...prev,
      type: event.target.value,
      document: '', // Limpa documento ao mudar tipo
    }));
  }

  async function handleDocumentBlur() {
    if (formData.type !== 'PJ') return;
    
    const cnpj = formData.document.replace(/\D/g, '');
    if (cnpj.length === 14) {
      try {
        const company = await Api.getCompanyByCnpj(cnpj);
        if (company) {
          setFormData(prev => ({
            ...prev,
            name: company.razao_social || prev.name,
            trade_name: company.nome_fantasia || prev.trade_name,
            zip_code: company.cep ? String(company.cep).replace(/(\d{5})(\d{3})/, '$1-$2') : prev.zip_code,
            street: company.logradouro || prev.street,
            number: company.numero || prev.number,
            complement: company.complemento || prev.complement,
            neighborhood: company.bairro || prev.neighborhood,
            city: company.municipio || prev.city,
            state: company.uf || prev.state,
            phone: company.ddd_telefone_1 || prev.phone,
            email: company.email || prev.email,
          }));
          toast.success('Dados do CNPJ carregados!');
        }
      } catch (err) {
        console.error('Erro ao buscar CNPJ', err);
      }
    }
  }

  async function handleCepBlur() {
    const cep = formData.zip_code.replace(/\D/g, '');
    if (cep.length === 8) {
      try {
        const address = await Api.getAddressByCep(cep);
        if (address && !address.erro) {
          setFormData(prev => ({
            ...prev,
            street: address.logradouro,
            neighborhood: address.bairro,
            city: address.localidade,
            state: address.uf,
          }));
        }
      } catch (err) {
        console.error('Erro ao buscar CEP', err);
        toast.error('Erro ao buscar CEP. Verifique se está correto.');
      }
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEdit) {
        const selectedCompanyId = formData.company_id || companyId;

        if (!selectedCompanyId) {
          toast.error('Selecione uma empresa.');
          setLoading(false);
          return;
        }

        const targetCompanyId = initialData.company_id || initialData.company?.id || companyId;
        await Api.updateSupplier(targetCompanyId, initialData.id, {
            ...formData,
            company_id: selectedCompanyId
        });
        toast.success('Fornecedor atualizado com sucesso!');
      } else {
        const cleanedCompanyIds = (selectedCompanyIds || []).filter(id => id !== 'all');

        if (cleanedCompanyIds.length === 0) {
          toast.error('Selecione ao menos uma empresa.');
          setLoading(false);
          return;
        }

        const createdSuppliers = [];

        for (const companyIdToCreate of cleanedCompanyIds) {
          const payload = {
            ...formData,
            company_id: companyIdToCreate,
          };
          // eslint-disable-next-line no-await-in-loop
          const created = await Api.createSupplier(companyIdToCreate, payload);
          createdSuppliers.push(created);
        }

        toast.success('Fornecedor(es) criado(s) com sucesso!');
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      const message = err.message || 'Erro ao salvar fornecedor.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="company-form-component" onSubmit={handleSubmit}>
      {error && <div className="form-error">{error}</div>}

      <div className="form-body">
        <h3 className="section-title" style={{ marginTop: '0px' }}>Dados Principais</h3>
        <div className="form-grid">
          {isEdit ? (
            <Select
              id="company_id"
              label="Empresa *"
              placeholder="SELECIONE UMA EMPRESA..."
              value={formData.company_id}
              onChange={handleChange}
              icon={FiBriefcase}
              options={[
                { value: '', label: 'SELECIONE UMA EMPRESA...' },
                ...companyOptions,
              ]}
              searchable
            />
          ) : (
            <MultiSelect
              id="companyIds"
              label="Empresas *"
              value={selectedCompanyIds}
              onChange={(newValues) => {
                const allValue = 'all';
                const currentSelected = selectedCompanyIds;
                const isAllSelected = newValues.includes(allValue);
                const wasAllSelected = currentSelected.includes(allValue);

                let nextValues;

                if (isAllSelected && !wasAllSelected) {
                  const allIds = companies.map(c => String(c.id));
                  nextValues = [allValue, ...allIds];
                } else if (!isAllSelected && wasAllSelected) {
                  nextValues = [];
                } else if (wasAllSelected && newValues.length < currentSelected.length) {
                  const remaining = newValues.filter(v => v !== allValue);
                  nextValues = remaining;
                } else {
                  const specificIds = newValues.filter(v => v !== allValue);
                  const allIds = companies.map(c => String(c.id));
                  if (specificIds.length === allIds.length && allIds.length > 0) {
                    nextValues = [allValue, ...specificIds];
                  } else {
                    nextValues = specificIds;
                  }
                }

                setSelectedCompanyIds(nextValues);
              }}
              icon={FiBriefcase}
              options={[
                { value: 'all', label: 'TODAS' },
                ...companyOptions,
              ]}
              searchable
            />
          )}

          <Select
            id="type"
            label="Tipo de Pessoa"
            value={formData.type}
            onChange={handleTypeChange}
            options={[
              { value: 'PF', label: 'Pessoa Física' },
              { value: 'PJ', label: 'Pessoa Jurídica' },
            ]}
            icon={FiUser}
            searchable
          />

          <Input
            id="document"
            label={formData.type === 'PJ' ? 'CNPJ' : 'CPF'}
            value={formData.document}
            onChange={handleChange}
            onBlur={handleDocumentBlur}
            mask={formData.type === 'PJ' ? 'cnpj' : 'cpf'}
            placeholder={formData.type === 'PJ' ? '00.000.000/0000-00' : '000.000.000-00'}
            icon={FiFileText}
          />
          
          <Input
            id="name"
            label={formData.type === 'PJ' ? 'Razão Social' : 'Nome Completo'}
            value={formData.name}
            onChange={handleChange}
            placeholder={formData.type === 'PJ' ? 'Razão Social da empresa' : 'Nome do fornecedor'}
            required
            icon={formData.type === 'PJ' ? FiBriefcase : FiUser}
          />

          {formData.type === 'PJ' && (
            <Input
              id="trade_name"
              label="Nome Fantasia"
              value={formData.trade_name}
              onChange={handleChange}
              placeholder="Nome comercial"
              icon={FiBriefcase}
            />
          )}

          <Select
            id="active"
            label="Status"
            value={String(formData.active)}
            onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === 'true' }))}
            options={[
              { value: 'true', label: 'Ativo' },
              { value: 'false', label: 'Inativo' },
            ]}
            icon={FiFlag}
            searchable
          />
          <div
            className={`feature-toggle-container ${formData.is_internal ? 'active' : ''}`}
            onClick={() =>
              setFormData(prev => ({
                ...prev,
                is_internal: !prev.is_internal,
              }))
            }
            style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}
          >
            <div className="toggle-track">
              <div className="toggle-thumb" />
            </div>
            <div className="toggle-content">
              <span className="toggle-title">Fornecedor interno?</span>
              <span className="toggle-description">
                Use para cadastros usados apenas em lançamentos internos (ajustes, adiantamentos, reembolsos, movimentações administrativas).
              </span>
            </div>
          </div>
        </div>

        <h3 className="section-title">Contato</h3>
        <div className="form-grid">
          <Input
            id="email"
            type="email"
            label="E-mail"
            value={formData.email}
            onChange={handleChange}
            placeholder="email@exemplo.com"
            icon={FiMail}
          />
          <Input
            id="phone"
            label="Telefone Fixo"
            value={formData.phone}
            onChange={handleChange}
            mask="phone"
            icon={FiPhone}
          />
          <Input
            id="mobile"
            label="Celular / WhatsApp"
            value={formData.mobile}
            onChange={handleChange}
            mask="phone"
            icon={FiSmartphone}
          />
        </div>

        <h3 className="section-title">Endereço</h3>
        <div className="form-grid">
          <Input
            id="zip_code"
            label="CEP"
            value={formData.zip_code}
            onChange={handleChange}
            onBlur={handleCepBlur}
            mask="cep"
            placeholder="00000-000"
            icon={FiMapPin}
          />
          <Input
            id="street"
            label="Logradouro"
            value={formData.street}
            onChange={handleChange}
            placeholder="Rua, Avenida, etc."
            icon={FiMapPin}
          />
          <Input
            id="number"
            label="Número"
            value={formData.number}
            onChange={handleChange}
            icon={FiHash}
          />
          <Input
            id="complement"
            label="Complemento"
            value={formData.complement}
            onChange={handleChange}
            icon={FiHome}
          />
          <Input
            id="neighborhood"
            label="Bairro"
            value={formData.neighborhood}
            onChange={handleChange}
            icon={FiMapPin}
          />
          <Input
            id="city"
            label="Cidade"
            value={formData.city}
            onChange={handleChange}
            icon={FiMapPin}
          />
          <Input
            id="state"
            label="UF"
            value={formData.state}
            onChange={handleChange}
            maxLength={2}
            icon={FiMapPin}
          />
        </div>

        <h3 className="section-title">Observações</h3>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
          <Input
            id="notes"
            label="Observações Gerais"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Informações adicionais..."
            icon={FiEdit}
          />
        </div>
      </div>

      <div className="form-actions">
        <Button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
